import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlHelpers } from '@bmng/helpers/url-helpers';
import { LangService } from '@seekda/angular-i18n';
import { Observable } from 'rxjs';

import { EndpointService } from '../endpoint.service';
import {
    InquiryV2,
    InquiryV2ListResult,
    InquiryV2OfferAvailability,
    InquiryV2OfferAvailabilityRequest,
    InquiryV2ReservationLookup,
    InquiryV2SearchFilters,
    InquiryV2SearchQuery,
    InquiryV2SortField,
    UpdateInquiryV2Request,
} from './interfaces/inquiries-v2.interface';
import { InquiriesV2ServiceInterface } from './interfaces/inquiries-v2.service.interface';

const SORT_COL_MAP: Record<string, InquiryV2SortField> = {
    contactInfo: 'contactLastName',
    contactName: 'contactLastName',
    checkIn: 'checkIn',
    checkOut: 'checkOut',
    status: 'status',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
    validityDate: 'validityDate',
};

@Injectable({ providedIn: 'root' })
export class InquiriesV2Service extends EndpointService implements InquiriesV2ServiceInterface {
    /**
     * Allow-listed so a filter id can never collide with a paging or sorting param on the backend's
     * search request, and so ids dropped in a later release stop being replayed from sessionStorage.
     */
    private static readonly FILTER_PARAM_KEYS: (keyof InquiryV2SearchFilters)[] = [
        'statuses',
        'checkInFrom',
        'checkInTo',
        'checkOutFrom',
        'checkOutTo',
        'createdAtFrom',
        'createdAtTo',
        'validityDateFrom',
        'validityDateTo',
    ];

    constructor(
        http: HttpClient,
        private readonly lang: LangService,
    ) {
        super(http);
    }

    getInquiries(hotelId: string, query: InquiryV2SearchQuery): Observable<InquiryV2ListResult> {
        const url = this.getBaseUrl(hotelId);

        const start = (query.page ?? 0) * (query.resultsPerPage ?? 20);
        const size = query.resultsPerPage ?? 20;

        const sortField: InquiryV2SortField = SORT_COL_MAP[query.sortCol] ?? 'createdAt';
        const sortType = query.sortDir === 'DESC' ? 'DESC' : 'ASC';

        const params: Record<string, unknown> = {
            start,
            size,
            sort: sortField,
            sortType,
            language: this.lang.getCurrentLanguage(),
        };

        if (query.searchTerm) {
            params.q = query.searchTerm;
        }

        this.applyFilterParams(params, query);

        return this.httpGet<InquiryV2ListResult>(UrlHelpers.buildUrl(url, params), EndpointService.HTTP_HEADERS);
    }

    /** Table filters arrive keyed by filter id under `query.filters`; `query.statuses` is the programmatic shortcut. */
    private applyFilterParams(params: Record<string, unknown>, query: InquiryV2SearchQuery): void {
        const filters: InquiryV2SearchFilters = { statuses: query.statuses, ...query.filters };

        for (const key of InquiriesV2Service.FILTER_PARAM_KEYS) {
            const value = filters[key];
            const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

            if (hasValue) {
                params[key] = value;
            }
        }
    }

    getInquiry(hotelId: string, inquiryId: number): Observable<InquiryV2> {
        const url = `${this.getBaseUrl(hotelId)}/${inquiryId}`;
        return this.httpGet<InquiryV2>(url, EndpointService.HTTP_HEADERS);
    }

    /** Forwards the v1 `inquiries/offer/<reservationId>` deep links from hotelier mails onto the v2 detail route. */
    getInquiryByReservationId(hotelId: string, reservationId: string): Observable<InquiryV2ReservationLookup> {
        const url = `${this.getBaseUrl(hotelId)}/by-reservation/${encodeURIComponent(reservationId)}`;
        return this.httpGet<InquiryV2ReservationLookup>(url, EndpointService.HTTP_HEADERS);
    }

    createInquiry(hotelId: string, data: UpdateInquiryV2Request): Observable<InquiryV2> {
        const url = this.getBaseUrl(hotelId);
        return this.httpPost<InquiryV2>(url, data, EndpointService.HTTP_HEADERS);
    }

    updateInquiry(hotelId: string, inquiryId: number, data: UpdateInquiryV2Request): Observable<InquiryV2> {
        const url = `${this.getBaseUrl(hotelId)}/${inquiryId}`;
        return this.httpPut<InquiryV2>(url, data, EndpointService.HTTP_HEADERS);
    }

    checkOfferAvailability(hotelId: string, data: InquiryV2OfferAvailabilityRequest): Observable<InquiryV2OfferAvailability[]> {
        const url = `${EndpointService.getBmBackendUrl()}/api/hotel/${hotelId}/inquiries/availability`;
        return this.httpPost<InquiryV2OfferAvailability[]>(url, data, EndpointService.HTTP_HEADERS);
    }

    cancelInquiry(hotelId: string, inquiryId: number): Observable<InquiryV2> {
        const url = `${this.getBaseUrl(hotelId)}/${inquiryId}/cancel`;
        return this.httpPost<InquiryV2>(url, {}, EndpointService.HTTP_HEADERS);
    }

    /** Books one offer option for the guest without guest checkout; the other options are cancelled. */
    bookInquiry(hotelId: string, inquiryId: number, reservationId: string, comment?: string): Observable<InquiryV2> {
        const url = `${this.getBaseUrl(hotelId)}/${inquiryId}/book`;
        return this.httpPost<InquiryV2>(url, { reservationId, comment: comment || null }, EndpointService.HTTP_HEADERS);
    }

    /** Backend URL the preview iframe posts the offer request to; the {inquiryId} variant also includes persisted comments. */
    getOfferPreviewUrl(hotelId: string, inquiryId: number | null, languageCode: string): string {
        const base = this.getBaseUrl(hotelId);
        const lang = (languageCode || 'en').toLowerCase();
        return inquiryId != null
            ? `${base}/${inquiryId}/preview/${lang}`
            : `${base}/preview/${lang}`;
    }

    private getBaseUrl(hotelId: string): string {
        return `${EndpointService.getBmBackendUrl()}/api/hotel/${hotelId}/inquiries/v2/manage`;
    }
}
